import React, { useEffect, useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section, SectionHeader } from "@/components/layout/Sections";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Trophy } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

// Blocchi storia Racing Team — copy fornito dal cliente
const STORY_BLOCKS = [
  {
    title: "La nascita del progetto",
    body: "NSA Motors Racing Team nasce nel 2024 dalla visione di **Alberto Di Rubba**, General Manager NSA Motors, con l'obiettivo di costruire una struttura solida, organizzata e orientata alla crescita dei giovani talenti dell'enduro.\n\nIl progetto prende forma da un'idea chiara: creare un team professionale, capace di unire metodo, disciplina e identità sportiva. Fin dall'inizio, l'impostazione è orientata allo sviluppo continuo, con una direzione precisa e ambizioni definite.",
    image: "/racing/alberto-di-rubba.png",
  },
  {
    title: "Il team",
    body: "A dicembre 2024 viene presentato ufficialmente NSA Motors Racing Team, con l'introduzione dei piloti e dello staff che affrontano la stagione 2025.\n\nIn questa fase vengono definite le linee guida operative, l'identità sportiva e gli obiettivi del progetto. Una struttura organizzata che rappresenta il primo passo verso un percorso di crescita progressivo e strutturato.",
    image: "/racing/story/foto2.webp",
  },
  {
    title: "Stagione 2025",
    body: "La stagione 2025 rappresenta il primo banco di prova del progetto, con la partecipazione ai principali campionati: Regionale, Italiano Under 23, Assoluti d'Italia ed Europeo.\n\nUna squadra giovane ma strutturata, costruita per crescere gara dopo gara e confrontarsi con contesti competitivi di alto livello, consolidando metodo, esperienza e risultati.",
    image: "/racing/story/foto3.webp",
    cta: {
      label: "Scopri la newsletter",
      href: "/racing/docs/stagione-2025.pdf",
    },
  },
  {
    title: "Un progetto costruito nei dettagli",
    body: "NSA Motors Racing Team è costruito su un'organizzazione completa che unisce performance sportiva e comunicazione.\n\nDalla cura delle moto all'abbigliamento gara, dalla logistica alla produzione di contenuti media, ogni elemento contribuisce a creare un'identità forte, riconoscibile e professionale. Un sistema integrato che rafforza la presenza del team in gara e nel contesto mediatico.",
    image: "/racing/racing-hero.jpg",
  },
  {
    title: "Campioni d'Italia 2025",
    body: "Grazie al lavoro della squadra e alla crescita costante dei piloti, NSA Motors Racing Team nel 2025 conquista il titolo italiano, affermandosi ai massimi livelli nazionali.\n\nUn risultato che conferma la solidità del progetto e la capacità del team di imporsi fin da subito nel panorama competitivo, dando continuità al percorso avviato.",
    image: "/racing/story/campioni-italia-2025-alt.png",
  },
  {
    title: "Il Mondiale",
    body: "Nel 2026 NSA Motors Racing Team compie un ulteriore salto di qualità con l'ingresso nel Campionato Mondiale di Enduro.\n\nUn passaggio naturale nel percorso di crescita del team, che segna l'inizio di una nuova fase internazionale, mantenendo al tempo stesso una presenza strutturata nei principali campionati nazionali.",
    image: "/racing/story/foto6.png",
  },
];

const PILOTS = [
  {
    slug: "manolo-morettini",
    name: "Manolo Morettini",
    photo: "/racing/manolo.webp",
    bio: "Sette volte campione italiano, vincitore del titolo ISDE Junior World Trophy nel 2021 e del World Trophy nel 2025. Originario della provincia di Bergamo, rappresenta non solo risultati ma anche identità e appartenenza. Un pilota che incarna pienamente i valori del team.",
    highlights: ["7× Campione Italiano", "ISDE Junior World Trophy 2021", "ISDE World Trophy 2025"],
  },
  {
    slug: "kevin-cristino",
    name: "Kevin Cristino",
    photo: "/racing/kevin.webp",
    bio: "Sei volte campione italiano, nel 2023 diventa campione del mondo Youth e nel 2024 conquista la World Cup J1 e la classe E3 alle ISDE. Nel 2025 ottiene il titolo Trophy alle ISDE, confermandosi come uno dei piloti più completi e competitivi a livello internazionale.",
    highlights: ["6× Campione Italiano", "Campione del Mondo Youth 2023", "World Cup J1 & E3 ISDE 2024", "ISDE Trophy 2025"],
  },
  {
    slug: "albin-norrbin",
    name: "Albin Norrbin",
    photo: "/racing/albin.webp",
    bio: "Campione del mondo Youth nel 2021, nel 2023 conquista il secondo posto nel Junior World Championship e nel 2024 vince il titolo Junior Trophy alle ISDE. Un pilota veloce, preciso e costantemente protagonista, capace di competere ai massimi livelli dello scenario internazionale.",
    highlights: ["Campione del Mondo Youth 2021", "2° Junior World Championship 2023", "ISDE Junior Trophy 2024"],
  },
];

// Piccolo parser markdown-lite: converte **testo** in <strong>testo</strong>
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i} className="font-semibold text-gray-900">{m[1]}</strong>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function StoryBlock({ block, index }) {
  const reverse = index % 2 === 1;
  const bgClass = block.imageMode === "portrait" ? "bg-contain bg-no-repeat bg-center bg-gray-50" : "bg-cover bg-top";
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}
      data-testid={`story-block-${index}`}
    >
      <div>
        <h3 className="font-display uppercase tracking-tight text-3xl md:text-4xl lg:text-5xl text-gray-900 leading-[0.95]">
          {block.title}
        </h3>
        <div className="mt-6 space-y-4">
          {block.body.split(/\n\n+/).map((p, j) => (
            <p key={j} className="text-lg leading-relaxed text-gray-700 font-body">
              {renderInline(p.trim())}
            </p>
          ))}
        </div>
        {block.cta && (
          <a
            href={block.cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 label-eyebrow px-7 py-4 bg-gray-900 text-white hover:bg-[color:var(--nsa-orange)] transition-colors"
            style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}
            data-testid={`story-cta-${index}`}
          >
            SCOPRI <ArrowRight size={16} />
          </a>
        )}
      </div>
      <div
        className={`aspect-[4/3] ${bgClass}`}
        style={{ backgroundImage: `url(${block.image})` }}
        data-testid={`story-image-${index}`}
      />
    </motion.div>
  );
}

function PilotCard({ pilot, index, onOpen }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="snap-start shrink-0 w-[420px] md:w-[440px] bg-gray-900 text-white overflow-hidden flex flex-col cursor-pointer"
      onClick={() => onOpen(pilot)}
      data-testid={`pilot-card-${pilot.slug}`}
    >
      <div className="relative aspect-[3/4] bg-cover bg-top" style={{ backgroundImage: `url(${pilot.photo})`, backgroundColor: "#0d0f12" }}>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="label-eyebrow text-[color:var(--nsa-orange)]">NSA Motors Racing Team</div>
          <h3 className="font-display uppercase tracking-tight text-3xl md:text-4xl mt-1 leading-[0.95]">
            {pilot.name}
          </h3>
        </div>
      </div>
      <div className="p-6 flex flex-col gap-5 flex-1">
        <ul className="grid grid-cols-1 gap-2 text-sm font-body">
          {pilot.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-white/90">
              <Trophy size={14} className="text-[color:var(--nsa-orange)] mt-1 shrink-0" /> {h}
            </li>
          ))}
        </ul>
        <p className="text-white/75 leading-relaxed text-sm">{pilot.bio}</p>
      </div>
    </motion.article>
  );
}

function PilotMiniCard({ pilot, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(pilot)}
      className="group text-left w-full"
      data-testid={`pilot-mini-${pilot.slug}`}
    >
      <div className="relative aspect-[3/4] bg-cover bg-top bg-gray-900" style={{ backgroundImage: `url(${pilot.photo})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-2">
          <div className="font-display uppercase text-white text-[11px] leading-tight tracking-tight" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>
            {pilot.name.split(" ")[0]}<br/>{pilot.name.split(" ").slice(1).join(" ")}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-gray-700 leading-tight">
        <Trophy size={11} className="text-[color:var(--nsa-orange)] mt-0.5 shrink-0" />
        <span>{pilot.highlights[0]}</span>
      </div>
      <div className="mt-1.5 label-eyebrow text-[9px] text-[color:var(--nsa-orange)] group-active:opacity-70">
        Leggi biografia →
      </div>
    </button>
  );
}

function PilotModal({ pilot, onClose }) {
  if (!pilot) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="pilot-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 text-white w-full sm:max-w-md max-h-[92vh] overflow-y-auto shadow-2xl"
      >
        <div className="relative aspect-[3/4] bg-cover bg-top" style={{ backgroundImage: `url(${pilot.photo})` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 h-10 w-10 flex items-center justify-center bg-black/60 text-white active:bg-black"
            aria-label="Chiudi"
            data-testid="pilot-modal-close"
          >
            ✕
          </button>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="label-eyebrow text-[color:var(--nsa-orange)] text-[10px]">NSA Motors Racing Team</div>
            <h3 className="font-display uppercase tracking-tight text-3xl mt-1 leading-[0.95]">
              {pilot.name}
            </h3>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <ul className="grid grid-cols-1 gap-2 text-sm font-body">
            {pilot.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-white/90">
                <Trophy size={14} className="text-[color:var(--nsa-orange)] mt-1 shrink-0" /> {h}
              </li>
            ))}
          </ul>
          <p className="text-white/80 leading-relaxed text-sm">{pilot.bio}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}


export default function RacingTeam() {
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [openPilot, setOpenPilot] = useState(null);
  const [visibleCount, setVisibleCount] = useState(9); // 9 cards initial (3 rows di 3)

  useEffect(() => {
    api.get("/news?category=racing").then((r) => setNews(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  return (
    <PageLayout>
      <Hero
        image="/racing/racing-hero.jpg"
        eyebrow="Moto · Racing Team"
        title="NSA Motors Racing Team"
      />

      {/* Storia — 6 blocchi */}
      <Section>
        <div className="max-w-3xl mb-14 md:mb-20">
          <span className="label-eyebrow text-[color:var(--nsa-orange)]">La nostra storia</span>
          <h2 className="font-display uppercase tracking-tight font-bold text-4xl md:text-5xl mt-3">
            Dal 2024 al Mondiale
          </h2>
          <p className="text-gray-600 mt-4 text-lg leading-relaxed">
            Il percorso completo del team, dai primi passi alla scena internazionale.
          </p>
        </div>
        <div className="space-y-20 md:space-y-28">
          {STORY_BLOCKS.map((block, i) => (
            <StoryBlock key={i} block={block} index={i} />
          ))}
        </div>
      </Section>

      {/* Team Mondiale */}
      <Section tone="gray">
        <SectionHeader
          eyebrow="Il vertice del progetto"
          title="Il Team Mondiale"
          description="Il Team Mondiale rappresenta il vertice del progetto NSA Motors Racing Team ed è impegnato nel Campionato del Mondo Enduro con una line-up composta da piloti di livello internazionale. Una squadra costruita per competere ai massimi livelli e rappresentare il team in un contesto globale, espressione della crescita e della solidità del progetto."
        />
        {/* Mobile: 3 mini card affiancate — tap apre il modal */}
        <div className="md:hidden grid grid-cols-3 gap-3" data-testid="pilots-mobile">
          {PILOTS.map((p) => (
            <PilotMiniCard key={p.slug} pilot={p} onOpen={setOpenPilot} />
          ))}
        </div>
        {/* Desktop: scroller orizzontale con card grandi */}
        <div className="hidden md:block -mx-4 md:-mx-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide" data-testid="pilots-scroller">
          <div className="flex gap-6 px-4 md:px-8 pb-4">
            {PILOTS.map((p, i) => (
              <PilotCard key={p.slug} pilot={p} index={i} onOpen={setOpenPilot} />
            ))}
          </div>
        </div>
      </Section>

      {/* Team Junior */}
      <Section tone="dark">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Una struttura su due livelli</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Il Team Junior
            </h2>
            <div className="mt-6 text-white/85 leading-relaxed space-y-4 font-body">
              <p>
                Il Team Junior rappresenta la <strong>base del progetto</strong> NSA Motors Racing Team e il punto di partenza del percorso sportivo.
              </p>
              <p>
                Attraverso la partecipazione agli <strong>Assoluti d'Italia</strong>, al <strong>Campionato Italiano Under 23</strong> e al <strong>Campionato Regionale</strong>,
                il team sviluppa giovani piloti, accompagnandoli in un percorso di crescita tecnica e sportiva strutturato.
              </p>
              <p>
                Un lavoro continuo che permette di costruire il futuro del team, valorizzando <strong>talento, disciplina e visione</strong>.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: "url(/racing/team-junior.jpg)" }} data-testid="team-junior-photo" />
        </div>
      </Section>

      {/* Gallery */}
      {gallery.length > 0 && (
      <Section tone="gray">
          <SectionHeader eyebrow="Gallery" title="I nostri momenti" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="aspect-square bg-cover bg-center card-flat"
                style={{ backgroundImage: `url(${mediaUrl(g.url || g.storage_path)})` }}
                title={g.caption}
                data-testid={`gallery-item-${i}`}
              />
            ))}
          </div>
        </Section>
      )}

      {/* News feed */}
      <Section tone="dark">
        <SectionHeader eyebrow="News Racing" title="Ultime notizie dal Racing Team" description="Aggiornamenti, gare, comunicati stampa e notizie." />
        {news.length === 0 ? (
          <div className="p-10 text-center text-white/60 border border-white/10" data-testid="news-empty">
            Nessuna news pubblicata al momento. Torna presto!
          </div>
        ) : (
          <>
            {/* Chronological grid — newest first */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.slice(0, visibleCount).map((n, i) => (
                <motion.article
                  key={n.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                  className="group bg-gray-800/40 hover:bg-gray-800/70 border border-white/10 overflow-hidden flex flex-col transition-colors"
                  data-testid={`news-card-${n.id}`}
                >
                  <Link to={`/moto/racing-team/news/${n.id}`} className="flex flex-col h-full">
                    {n.cover_image ? (
                      <div className="aspect-[16/10] overflow-hidden">
                        <div
                          className="w-full h-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                          style={{ backgroundImage: `url(${mediaUrl(n.cover_image)})` }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] bg-gray-900" />
                    )}
                    <div className="p-6 flex-1 flex flex-col">
                      <span className="label-eyebrow text-white/40">
                        {new Date(n.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      <h3 className="font-display uppercase text-lg md:text-xl mt-2 leading-tight text-white line-clamp-3">{n.title}</h3>
                      <p className="text-white/60 mt-3 text-sm leading-relaxed flex-1 line-clamp-3">{n.excerpt || (n.content || n.body || "").slice(0, 200)}</p>
                      <span className="mt-4 label-eyebrow text-[color:var(--nsa-orange)] inline-flex items-center gap-2">
                        Leggi di più <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {/* Load more */}
            {visibleCount < news.length && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => Math.min(c + 6, news.length))}
                  className="label-eyebrow px-8 py-4 bg-white text-gray-900 hover:bg-[color:var(--nsa-orange)] hover:text-white transition-colors"
                  data-testid="news-load-more"
                >
                  Carica altre news ({news.length - visibleCount} rimanenti)
                </button>
              </div>
            )}
          </>
        )}
      </Section>

      <AnimatePresence>
        {openPilot && <PilotModal pilot={openPilot} onClose={() => setOpenPilot(null)} />}
      </AnimatePresence>
    </PageLayout>
  );
}
